<template>
    <div class="admin-visits">
        <!-- 概览 -->
        <div class="v-stats">
            <div class="v-stat">
                <div class="s-label">今日访问（PV）</div>
                <div class="s-value">{{ stats.todayPv }}</div>
                <div class="s-foot">昨天 {{ stats.yesterdayPv }}</div>
            </div>
            <div class="v-stat">
                <div class="s-label">今日访客（UV）</div>
                <div class="s-value">{{ stats.todayUv }}</div>
                <div class="s-foot">去重后的访客数</div>
            </div>
            <div class="v-stat">
                <div class="s-label">今日独立 IP</div>
                <div class="s-value">{{ stats.todayIp }}</div>
                <div class="s-foot">同一网络算一个</div>
            </div>
            <div class="v-stat">
                <div class="s-label">累计访问</div>
                <div class="s-value">{{ stats.totalPv }}</div>
                <div class="s-foot">累计访客 {{ stats.totalUv }} · IP {{ stats.totalIp }}</div>
            </div>
            <div class="v-stat trend">
                <div class="s-label">近 7 日</div>
                <div class="v-bars">
                    <div
                        v-for="d in stats.trend"
                        :key="d.date"
                        class="bar"
                        :title="d.date + '：' + d.pv + ' 次 / ' + d.uv + ' 人'"
                    >
                        <span class="b-fill" :style="{ height: barHeight(d.pv) }"></span>
                        <span class="b-label">{{ d.date.slice(5) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 列表 -->
        <div class="a-card">
            <div class="a-toolbar">
                <input
                    class="a-input"
                    style="width: 200px"
                    v-model="query.keyword"
                    placeholder="账号 / 昵称 / IP / 路径"
                    @keyup.enter="search"
                />
                <select class="a-select" v-model="query.source" @change="search">
                    <option value="all">全部来源</option>
                    <option value="page">页面浏览</option>
                    <option value="entry">打开站点</option>
                    <option value="admin">后台登录</option>
                    <option value="api">接口访问</option>
                </select>
                <select class="a-select" v-model="query.device" @change="search">
                    <option value="all">全部设备</option>
                    <option value="pc">电脑</option>
                    <option value="mobile">手机</option>
                    <option value="tablet">平板</option>
                </select>
                <select
                    class="a-select"
                    style="max-width: 150px"
                    v-model="query.account"
                    @change="search"
                >
                    <option value="all">全部账号</option>
                    <option v-for="a in facets.accounts" :key="a" :value="a">{{ a }}</option>
                </select>
                <span class="date-range">
                    <input class="a-input" type="date" v-model="query.startDate" @change="search" />
                    <em>~</em>
                    <input class="a-input" type="date" v-model="query.endDate" @change="search" />
                </span>
                <button class="a-btn primary" @click="search">查询</button>
                <button class="a-btn" @click="reset">重置</button>

                <span class="grow"></span>

                <button class="a-btn" :disabled="loading" @click="load(true)">刷新</button>
                <button class="a-btn" :disabled="exporting" @click="exportCsv">
                    {{ exporting ? '导出中…' : '导出 CSV' }}
                </button>
                <button class="a-btn danger" @click="clearOpen = true">清理记录</button>
            </div>

            <div v-if="loading" class="a-empty">正在加载…</div>
            <div v-else-if="!items.length" class="a-empty">
                还没有访问记录。用户打开前台页面后就会自动记录（IP、账号、时间、访问页面）。
            </div>
            <table v-else class="a-table">
                <thead>
                    <tr>
                        <th style="width: 148px">时间</th>
                        <th style="width: 84px">来源</th>
                        <th style="width: 132px">账号</th>
                        <th style="width: 138px">IP / 归属</th>
                        <th style="width: 190px">访问页面</th>
                        <th>标题</th>
                        <th style="width: 150px">设备</th>
                        <th style="width: 74px">停留</th>
                        <th style="width: 90px">操作</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="it in items" :key="it.id">
                        <td class="muted small">{{ it.createTime }}</td>
                        <td>
                            <span class="v-src" :class="'src-' + it.source">{{ srcLabel(it.source) }}</span>
                        </td>
                        <td>
                            <div v-if="it.account" class="who">
                                <span class="w-name">{{ it.nickname || it.account }}</span>
                                <span class="w-acc" v-if="it.nickname && it.nickname !== it.account">{{ it.account }}</span>
                            </div>
                            <span v-else class="muted">游客</span>
                        </td>
                        <td>
                            <div class="ip-cell">
                                <code>{{ it.ip || '—' }}</code>
                                <span v-if="it.region" class="region">{{ it.region }}</span>
                            </div>
                        </td>
                        <td><code class="path">{{ it.path || '—' }}</code></td>
                        <td class="muted ellipsis" :title="it.title">{{ it.title || '—' }}</td>
                        <td class="muted small">
                            {{ deviceLabel(it.device) }}
                            <template v-if="it.browser"> · {{ it.browser }}</template>
                            <template v-if="it.os"> · {{ it.os }}</template>
                        </td>
                        <td class="muted small">{{ stayLabel(it.stayMs) }}</td>
                        <td class="col-ops">
                            <button class="a-btn mini" @click="detail(it)">详情</button>
                            <button class="a-btn mini danger" @click="remove(it)">删除</button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="a-pager" v-if="total > query.size">
                <span>共 {{ total }} 条 · 第 {{ query.page }}/{{ pages }} 页</span>
                <button class="a-btn mini" :disabled="query.page <= 1" @click="go(query.page - 1)">上一页</button>
                <button class="a-btn mini" :disabled="query.page >= pages" @click="go(query.page + 1)">下一页</button>
            </div>
        </div>

        <!-- 热门 / 活跃 -->
        <div class="v-ranks">
            <div class="a-card rank">
                <div class="rank-head">热门页面 Top 10</div>
                <div v-if="!stats.topPages.length" class="rank-empty">暂无数据</div>
                <ul v-else>
                    <li v-for="(p, i) in stats.topPages" :key="p.name">
                        <span class="r-no" :class="{ top3: i < 3 }">{{ i + 1 }}</span>
                        <code class="r-name">{{ p.name }}</code>
                        <span class="r-count">{{ p.count }}</span>
                    </li>
                </ul>
            </div>
            <div class="a-card rank">
                <div class="rank-head">活跃账号 Top 10</div>
                <div v-if="!stats.topAccounts.length" class="rank-empty">暂无数据（未登录访问记为「游客」）</div>
                <ul v-else>
                    <li v-for="(p, i) in stats.topAccounts" :key="p.name">
                        <span class="r-no" :class="{ top3: i < 3 }">{{ i + 1 }}</span>
                        <span class="r-name acc">{{ p.name }}</span>
                        <span class="r-count">{{ p.count }}</span>
                    </li>
                </ul>
            </div>
        </div>

        <!-- 详情弹窗 -->
        <div v-if="current" class="v-mask" @click.self="current = null">
            <div class="v-modal">
                <div class="m-head">
                    <h3>访问详情 #{{ current.id }}</h3>
                    <span class="m-close" @click="current = null">×</span>
                </div>
                <div class="m-body">
                    <div v-for="row in detailRows" :key="row.k" class="m-row">
                        <span class="m-k">{{ row.k }}</span>
                        <span class="m-v">{{ row.v || '—' }}</span>
                    </div>
                </div>
                <div class="m-foot">
                    <button class="a-btn" @click="current = null">关闭</button>
                </div>
            </div>
        </div>

        <!-- 清理弹窗 -->
        <div v-if="clearOpen" class="v-mask" @click.self="clearOpen = false">
            <div class="v-modal narrow">
                <div class="m-head">
                    <h3>清理访问记录</h3>
                    <span class="m-close" @click="clearOpen = false">×</span>
                </div>
                <div class="m-body">
                    <div class="a-form-row">
                        <label class="f-label">清理方式</label>
                        <select class="a-select" v-model="clearForm.mode">
                            <option value="days">只保留最近 N 天</option>
                            <option value="before">清空指定日期之前</option>
                            <option value="all">全部清空</option>
                        </select>
                    </div>
                    <div class="a-form-row" v-if="clearForm.mode === 'days'">
                        <label class="f-label">保留天数</label>
                        <input class="a-input" type="number" min="1" max="3650" v-model="clearForm.days" />
                    </div>
                    <div class="a-form-row" v-if="clearForm.mode === 'before'">
                        <label class="f-label">清空这个日期之前的记录<span class="f-tip">（不含当天）</span></label>
                        <input class="a-input" type="date" v-model="clearForm.date" />
                    </div>
                    <p class="danger-tip">删除后不可恢复，请谨慎操作。</p>
                </div>
                <div class="m-foot">
                    <button class="a-btn" @click="clearOpen = false">取消</button>
                    <button class="a-btn danger" :disabled="clearing" @click="doClear">
                        {{ clearing ? '清理中…' : '确认清理' }}
                    </button>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div v-if="toast" class="a-toast" :class="toast.type">{{ toast.text }}</div>
        </transition>
    </div>
</template>

<script>
import { visitApi, getToken } from './api';

const SRC_LABEL = { page: '页面浏览', entry: '打开站点', admin: '后台登录', api: '接口访问' };
const DEVICE_LABEL = { pc: '电脑', mobile: '手机', tablet: '平板' };

function today(offsetDays) {
    const d = new Date();
    if (offsetDays) {
        d.setDate(d.getDate() + offsetDays);
    }
    const p = n => (n < 10 ? '0' + n : '' + n);
    return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

export default {
    name: 'AdminVisits',
    data() {
        return {
            loading: false,
            exporting: false,
            clearing: false,
            items: [],
            total: 0,
            pages: 1,
            stats: {
                todayPv: 0,
                todayUv: 0,
                todayIp: 0,
                totalPv: 0,
                totalUv: 0,
                totalIp: 0,
                yesterdayPv: 0,
                trend: [],
                topPages: [],
                topAccounts: []
            },
            facets: { accounts: [], ips: [], paths: [] },
            query: {
                keyword: '',
                source: 'all',
                device: 'all',
                account: 'all',
                startDate: '',
                endDate: '',
                order: 'desc',
                page: 1,
                size: 20
            },
            current: null,
            clearOpen: false,
            clearForm: { mode: 'days', days: 30, date: today(-30) },
            toast: null,
            toastTimer: null
        };
    },
    computed: {
        detailRows() {
            const c = this.current;
            if (!c) {
                return [];
            }
            return [
                { k: '访问时间', v: c.createTime },
                { k: '来源', v: SRC_LABEL[c.source] || c.source },
                { k: '账号', v: c.account },
                { k: '昵称', v: c.nickname },
                { k: '角色', v: c.role },
                { k: 'IP', v: c.ip },
                { k: 'IP 归属', v: c.region },
                { k: '访问路径', v: c.path },
                { k: '页面标题', v: c.title },
                { k: '内容页标识', v: c.pageCode },
                { k: '设备', v: DEVICE_LABEL[c.device] || c.device },
                { k: '浏览器', v: c.browser },
                { k: '操作系统', v: c.os },
                { k: '屏幕', v: c.screen },
                { k: '停留时长', v: this.stayLabel(c.stayMs) },
                { k: '状态码', v: c.statusCode },
                { k: '访客标识', v: c.visitor },
                { k: '来源页', v: c.referer },
                { k: 'User-Agent', v: c.userAgent }
            ];
        }
    },
    created() {
        this.load();
        this.loadStats();
        this.loadFacets();
    },
    methods: {
        srcLabel(s) {
            return SRC_LABEL[s] || s || '—';
        },
        deviceLabel(d) {
            return DEVICE_LABEL[d] || d || '—';
        },
        stayLabel(ms) {
            const n = parseInt(ms, 10) || 0;
            if (n <= 0) {
                return '—';
            }
            if (n < 1000) {
                return n + 'ms';
            }
            const s = Math.round(n / 1000);
            if (s < 60) {
                return s + 's';
            }
            const m = Math.floor(s / 60);
            const rs = s % 60;
            return m + 'm' + (rs ? rs + 's' : '');
        },
        barHeight(pv) {
            const max = (this.stats.trend || []).reduce((a, b) => Math.max(a, b.pv), 0);
            if (!max) {
                return '2px';
            }
            return Math.max(2, Math.round((pv / max) * 46)) + 'px';
        },
        notify(text, type) {
            this.toast = { text: text, type: type || 'ok' };
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => {
                this.toast = null;
            }, 2400);
        },
        params() {
            return {
                keyword: this.query.keyword,
                source: this.query.source,
                device: this.query.device,
                account: this.query.account,
                startDate: this.query.startDate,
                endDate: this.query.endDate,
                order: this.query.order
            };
        },
        load(silent) {
            this.loading = !silent;
            return visitApi
                .list(Object.assign(this.params(), { page: this.query.page, size: this.query.size }))
                .then(data => {
                    this.items = data.items || [];
                    this.total = data.total || 0;
                    this.pages = data.pages || 1;
                })
                .catch(err => {
                    if (err.code === 401 || !getToken()) {
                        this.$router.replace('/admin/login');
                        return;
                    }
                    this.notify(err.message, 'err');
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadStats() {
            visitApi
                .stats()
                .then(d => {
                    this.stats = Object.assign(this.stats, d || {});
                    this.stats.trend = (d && d.trend) || [];
                    this.stats.topPages = (d && d.topPages) || [];
                    this.stats.topAccounts = (d && d.topAccounts) || [];
                })
                .catch(() => {});
        },
        loadFacets() {
            visitApi
                .facets()
                .then(d => {
                    this.facets = d || this.facets;
                })
                .catch(() => {});
        },
        search() {
            this.query.page = 1;
            this.load();
        },
        reset() {
            this.query.keyword = '';
            this.query.source = 'all';
            this.query.device = 'all';
            this.query.account = 'all';
            this.query.startDate = '';
            this.query.endDate = '';
            this.query.page = 1;
            this.load();
        },
        go(p) {
            if (p < 1 || p > this.pages) {
                return;
            }
            this.query.page = p;
            this.load();
        },
        detail(it) {
            this.current = it;
        },
        remove(it) {
            if (!window.confirm('确定删除这条访问记录吗？')) {
                return;
            }
            visitApi
                .remove(it.id)
                .then(() => {
                    this.notify('已删除');
                    this.load(true);
                    this.loadStats();
                })
                .catch(err => this.notify(err.message, 'err'));
        },
        doClear() {
            const mode = this.clearForm.mode;
            if (mode === 'before' && !this.clearForm.date) {
                this.notify('请选择日期', 'err');
                return;
            }
            const tip = mode === 'all' ? '确定要清空全部访问记录吗？该操作不可恢复。' : '确定按条件清理访问记录吗？';
            if (!window.confirm(tip)) {
                return;
            }
            this.clearing = true;
            visitApi
                .clear({ mode: mode, days: parseInt(this.clearForm.days, 10) || 0, date: this.clearForm.date })
                .then(data => {
                    this.notify('已清理 ' + ((data && data.removed) || 0) + ' 条');
                    this.clearOpen = false;
                    this.query.page = 1;
                    this.load();
                    this.loadStats();
                    this.loadFacets();
                })
                .catch(err => this.notify(err.message, 'err'))
                .finally(() => {
                    this.clearing = false;
                });
        },
        exportCsv() {
            this.exporting = true;
            visitApi
                .export(Object.assign(this.params(), { limit: 5000 }))
                .then(data => {
                    const rows = (data && data.items) || [];
                    if (!rows.length) {
                        this.notify('没有可导出的记录', 'err');
                        return;
                    }
                    const head = [
                        '时间', '来源', '账号', '昵称', 'IP', 'IP归属', '访问路径', '页面标题',
                        '设备', '浏览器', '系统', '屏幕', '停留(ms)', '状态码', '访客标识', '来源页', 'User-Agent'
                    ];
                    const cell = v => {
                        const s = v === null || v === undefined ? '' : String(v);
                        return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
                    };
                    const lines = [head.join(',')];
                    rows.forEach(r => {
                        lines.push(
                            [
                                r.createTime, this.srcLabel(r.source), r.account, r.nickname, r.ip, r.region,
                                r.path, r.title, this.deviceLabel(r.device), r.browser, r.os, r.screen,
                                r.stayMs, r.statusCode, r.visitor, r.referer, r.userAgent
                            ]
                                .map(cell)
                                .join(',')
                        );
                    });
                    // 带 BOM，Excel 打开中文不乱码
                    const blob = new window.Blob(['\ufeff' + lines.join('\r\n')], {
                        type: 'text/csv;charset=utf-8'
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = '访问记录_' + today() + '.csv';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    this.notify('已导出 ' + rows.length + ' 条');
                })
                .catch(err => this.notify(err.message, 'err'))
                .finally(() => {
                    this.exporting = false;
                });
        }
    }
};
</script>

<style lang="less" scoped>
.admin-visits {
    .grow {
        flex: 1;
    }
    .muted {
        color: #8a7a6b;
    }
    .small {
        font-size: 12px;
    }
    .date-range {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        em {
            font-style: normal;
            color: #a89383;
        }
        .a-input {
            width: 132px;
        }
    }
}

/* ---------------- 概览卡片 ---------------- */
.v-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-bottom: 16px;
}
.v-stat {
    flex: 1 1 150px;
    min-width: 150px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #ebdfd0;
    border-radius: 12px;
    box-shadow: 0 6px 20px rgba(120, 86, 44, 0.06);
    .s-label {
        font-size: 12.5px;
        color: #8a7360;
    }
    .s-value {
        margin-top: 6px;
        font-size: 26px;
        font-weight: 700;
        color: #3f3126;
        line-height: 1.2;
    }
    .s-foot {
        margin-top: 6px;
        font-size: 11.5px;
        color: #b0a094;
    }
    &.trend {
        flex: 1 1 300px;
    }
}
.v-bars {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    height: 62px;
    margin-top: 4px;
    .bar {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-end;
        height: 100%;
        .b-fill {
            display: block;
            width: 100%;
            max-width: 26px;
            border-radius: 4px 4px 2px 2px;
            background: linear-gradient(180deg, #e0556f, #b71c3a);
            transition: height 0.3s;
        }
        .b-label {
            margin-top: 4px;
            font-size: 10.5px;
            color: #a89383;
        }
    }
}

/* ---------------- 来源标签 ---------------- */
.v-src {
    display: inline-block;
    padding: 2px 8px;
    font-size: 11.5px;
    line-height: 18px;
    border-radius: 6px;
    white-space: nowrap;
    &.src-page {
        color: #1c7a4a;
        background: #e7f6ee;
        border: 1px solid #c4e8d5;
    }
    &.src-entry {
        color: #8a6a2a;
        background: #fdf4e2;
        border: 1px solid #f0e0bc;
    }
    &.src-admin {
        color: #a3122e;
        background: #fdeef1;
        border: 1px solid #f4ccd5;
    }
    &.src-api {
        color: #7a6553;
        background: #f1ece6;
        border: 1px solid #e2d8cd;
    }
}

.who {
    line-height: 1.4;
    .w-name {
        display: block;
        font-size: 13px;
        color: #3f3126;
    }
    .w-acc {
        display: block;
        font-size: 11.5px;
        color: #a89383;
    }
}
.ip-cell {
    line-height: 1.4;
    code {
        font-size: 12.5px;
        color: #5b4636;
    }
    .region {
        display: block;
        font-size: 11px;
        color: #b0a094;
    }
}
.path {
    font-size: 12.5px;
    color: #8a6a52;
    word-break: break-all;
}
.ellipsis {
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ---------------- Top 榜 ---------------- */
.v-ranks {
    display: flex;
    gap: 14px;
    margin-top: 16px;
    .rank {
        flex: 1;
        min-width: 0;
        padding-bottom: 8px;
    }
    .rank-head {
        padding: 13px 18px;
        font-size: 13px;
        font-weight: 600;
        color: #4a3629;
        border-bottom: 1px solid #f0e6da;
    }
    .rank-empty {
        padding: 26px 18px;
        font-size: 12.5px;
        color: #a89383;
    }
    ul {
        margin: 0;
        padding: 6px 0;
        list-style: none;
    }
    li {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 6px 18px;
        font-size: 12.5px;
        &:hover {
            background: #fdfaf5;
        }
    }
    .r-no {
        width: 18px;
        height: 18px;
        flex: none;
        text-align: center;
        line-height: 18px;
        font-size: 11px;
        color: #a89383;
        background: #f4ece2;
        border-radius: 4px;
        &.top3 {
            color: #fff;
            background: linear-gradient(140deg, #cf1f3d, #a3122e);
        }
    }
    .r-name {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #5b4636;
        &.acc {
            color: #3f3126;
        }
    }
    .r-count {
        color: #a3122e;
        font-weight: 600;
    }
}

/* ---------------- 弹窗 ---------------- */
.v-mask {
    position: fixed;
    inset: 0;
    z-index: 9000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(47, 38, 32, 0.42);
}
.v-modal {
    width: 620px;
    max-width: 92vw;
    max-height: 84vh;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 14px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.22);
    overflow: hidden;
    &.narrow {
        width: 420px;
    }
    .m-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        border-bottom: 1px solid #f0e6da;
        h3 {
            margin: 0;
            font-size: 15px;
            font-weight: 600;
            color: #4a3629;
        }
        .m-close {
            font-size: 22px;
            line-height: 1;
            color: #a89383;
            cursor: pointer;
            &:hover {
                color: #a3122e;
            }
        }
    }
    .m-body {
        flex: 1;
        overflow: auto;
        padding: 16px 20px;
    }
    .m-row {
        display: flex;
        padding: 7px 0;
        font-size: 12.5px;
        border-bottom: 1px dashed #f2e9dd;
        .m-k {
            width: 92px;
            flex: none;
            color: #a89383;
        }
        .m-v {
            flex: 1;
            min-width: 0;
            color: #4a3f35;
            word-break: break-all;
        }
    }
    .m-foot {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 14px 20px;
        border-top: 1px solid #f0e6da;
    }
}
.danger-tip {
    margin: 4px 0 0;
    font-size: 12px;
    color: #a3122e;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
